import { useTranslation } from 'react-i18next';

import { EvaluationPager, useEvaluationPager } from './EvaluationPager';
import {
    EVALUATION_LIST_ITEM,
    EVALUATION_LIST_ROW,
    EVALUATION_PANEL_INNER_LIST,
    EVALUATION_PANEL_INNER_SURFACE,
    EvaluationPanel,
} from './EvaluationPanel';
import { EvaluationStartButton } from './EvaluationStartButton';
import { useStartEvaluation } from './useStartEvaluation';

import { useGetUserById } from '@/api/directory';
import { useGetSelfAssessments } from '@/api/performance-profile';
import { AssessmentGroupIllustration } from '@/components/Illustrations/Illustration';
import { TileLink } from '@/components/TileLink';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { evaluationDestination } from '@/navigation/evaluationDestination';

/** The reference's own 116px width; the height follows the drawing's ratio, as the module does. */
const ILLUSTRATION_SIZE = 'h-auto w-29';

interface SelfEvaluationPanelProps {
    employeeId?: string;
    isEmployeeInactive?: boolean;
    canStartEvaluations?: boolean;
    className?: string;
}

export const SelfEvaluationPanel = ({
    employeeId,
    isEmployeeInactive,
    canStartEvaluations,
    className,
}: SelfEvaluationPanelProps) => {
    const { t } = useTranslation();
    const { start: startEvaluation, isCreating } = useStartEvaluation({
        evaluationType: EvaluationType.SELF_EVALUATION,
        firstStep: CycleInstanceSteps.SELECT_TEMPLATE,
    });

    const { data: assessments, isLoading, error } = useGetSelfAssessments(employeeId);
    const { data: employeeUser } = useGetUserById({ userId: employeeId ?? '' });
    const employeeName = employeeUser?.firstName ?? '';
    const { getManagerLabel } = useCompanyInformation();

    const { pageIndex, setPageIndex, pageCount, visible } = useEvaluationPager(assessments, 4);

    return (
        <EvaluationPanel
            title={t('performanceProfile.evaluations.self.title')}
            description={t('performanceProfile.evaluations.self.description', {
                name: employeeName,
                managerLabel: getManagerLabel(),
            })}
            className={className}
            illustration={<AssessmentGroupIllustration className={ILLUSTRATION_SIZE} />}
        >
            {isLoading ? (
                <div className={EVALUATION_PANEL_INNER_SURFACE}>
                    <div className={'text-obj-gray text-[14px]'}>
                        {t('performanceProfile.evaluations.loading')}
                    </div>
                </div>
            ) : error ? (
                <div className={EVALUATION_PANEL_INNER_SURFACE}>
                    <div className={'text-error text-[14px]'}>
                        {t('performanceProfile.evaluations.self.loadError')}
                    </div>
                </div>
            ) : !assessments || assessments.length === 0 ? (
                <div className={EVALUATION_PANEL_INNER_SURFACE}>
                    <div className={'text-obj-gray text-[14px]'}>
                        {t('performanceProfile.evaluations.self.noAssessments')}
                    </div>
                </div>
            ) : (
                <ul className={EVALUATION_PANEL_INNER_LIST}>
                    {visible.map((entry) => (
                        <li
                            key={entry.reviewInstanceId}
                            className={EVALUATION_LIST_ITEM}
                        >
                            <TileLink
                                to={evaluationDestination({
                                    reviewInstanceId: entry.reviewInstanceId,
                                    cycleInstanceId: entry.cycleInstanceId,
                                })}
                                ariaLabel={t('performanceProfile.evaluations.self.openLabel', {
                                    date: entry.date,
                                })}
                                className={EVALUATION_LIST_ROW}
                            >
                                <span>{entry.date}</span>
                                <span>{entry.name}</span>
                            </TileLink>
                        </li>
                    ))}
                </ul>
            )}

            <EvaluationPager
                pageIndex={pageIndex}
                pageCount={pageCount}
                onPickPage={setPageIndex}
            />

            {!isEmployeeInactive && (
                <EvaluationStartButton
                    canStartEvaluations={canStartEvaluations}
                    disabled={isCreating}
                    onClick={() => startEvaluation(employeeId)}
                    tooltip={t('performanceProfile.evaluations.disabledTooltip')}
                    iconName={'arrow-export-3'}
                >
                    {t('performanceProfile.evaluations.self.startButton')}
                </EvaluationStartButton>
            )}
        </EvaluationPanel>
    );
};
